import React, { useEffect, useRef } from 'react';

export default function InteractiveCandleBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    let mouseX = -1000;
    let mouseY = -1000;
    let scrollOffset = 0;

    const candleWidth = 12;
    const gap = 16;
    const totalCandles = Math.ceil(width / (candleWidth + gap)) + 80;

    const candles = [];
    let currentY = height * 0.5;

    for (let i = 0; i < totalCandles; i++) {
      const open = currentY + (Math.random() - 0.5) * 30;
      const close = open + (Math.random() - 0.48) * 40;
      const high = Math.min(open, close) - Math.random() * 20;
      const low = Math.max(open, close) + Math.random() * 20;

      const isBullish = close < open;
      candles.push({ open, close, high, low, isBullish });

      currentY = close;
      if (currentY < height * 0.25) currentY = height * 0.35;
      if (currentY > height * 0.75) currentY = height * 0.65;
    }

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const handleScroll = () => {
      scrollOffset = window.scrollY * 0.3;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // 1. Light Warm Background Fill (#FAF8F5)
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(0, 0, width, height);

      // 2. Subtle Light Grid Lines
      ctx.strokeStyle = 'rgba(10, 37, 64, 0.03)';
      ctx.lineWidth = 1;
      const gridSize = 45;

      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // 3. Mouse Radial Light Spotlight (Blue Palette)
      if (mouseX >= 0 && mouseY >= 0) {
        const gradient = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 300);
        gradient.addColorStop(0, 'rgba(37, 99, 235, 0.08)');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      }

      // 4. Subtle Candlesticks (Navy Blue for Bullish, Burgundy for Bearish)
      const step = candleWidth + gap;

      candles.forEach((c, index) => {
        const x = index * step - (scrollOffset % (candles.length * step));

        if (x < -candleWidth || x > width + candleWidth) return;

        const distToMouse = Math.hypot(x - mouseX, c.open - mouseY);
        const isHovered = distToMouse < 140;

        // Base Subtle Colors
        const blueBullishColor = 'rgba(22, 50, 91, 0.16)'; // Deep Navy Blue Accent
        const redBearishColor = 'rgba(153, 27, 27, 0.10)';  // Dark Burgundy Subtle

        // Hover Vibrant Colors
        const hoverBlue = 'rgba(37, 99, 235, 0.70)';
        const hoverRed = 'rgba(239, 68, 68, 0.45)';

        ctx.save();

        if (isHovered) {
          ctx.shadowColor = c.isBullish ? 'rgba(37, 99, 235, 0.35)' : 'rgba(239, 68, 68, 0.3)';
          ctx.shadowBlur = 10;
          ctx.strokeStyle = c.isBullish ? hoverBlue : hoverRed;
          ctx.fillStyle = c.isBullish ? hoverBlue : hoverRed;
        } else {
          ctx.shadowBlur = 0;
          ctx.strokeStyle = c.isBullish ? blueBullishColor : redBearishColor;
          ctx.fillStyle = c.isBullish ? blueBullishColor : redBearishColor;
        }

        // Draw Wick
        ctx.beginPath();
        ctx.lineWidth = 1.2;
        ctx.moveTo(x + candleWidth / 2, c.high);
        ctx.lineTo(x + candleWidth / 2, c.low);
        ctx.stroke();

        // Draw Candle Body
        const bodyY = Math.min(c.open, c.close);
        const bodyHeight = Math.max(Math.abs(c.open - c.close), 3);
        ctx.fillRect(x, bodyY, candleWidth, bodyHeight);

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
}